import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Header from './Header';
import ClientHeader from './ClientHeader';
import AppShell from './shell/AppShell';
import PhoneNumberManager from './PhoneNumberManager';
import SettingsHub from './SettingsHub';
import AIUsagePanel from './AIUsagePanel';
import AITaggingPanel from './AITaggingPanel';
import { API_URL } from '../config/api';

const API = API_URL;

const PERMISSION_DEFS = [
  { key: 'create_searches', label: 'Create Searches', desc: 'Create new searches and projects' },
  { key: 'manage_candidates', label: 'Manage Candidates', desc: 'Add, edit, delete candidates' },
  { key: 'import_candidates', label: 'Import Candidates', desc: 'Bulk import via CSV' },
  { key: 'manage_client_firms', label: 'Manage Client Firms', desc: 'Create firms, manage partnerships' },
  { key: 'schedule_meetings', label: 'Schedule Meetings', desc: 'Create and send calendar invites' },
  { key: 'approve_reject', label: 'Approve / Reject', desc: 'Approve or reject candidates' },
  { key: 'view_compensation', label: 'View Compensation', desc: 'See candidate compensation data' },
  { key: 'export_data', label: 'Export Data', desc: 'Export candidate lists to CSV' },
  { key: 'upload_files', label: 'Upload Files', desc: 'Upload presentation and collaboration files' },
  { key: 'manage_stages', label: 'Manage Stages', desc: 'Add, edit, delete pipeline stages' },
];

const UserManagementPage = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [saving, setSaving] = useState(null);
  const [filter, setFilter] = useState('active');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteData, setInviteData] = useState({ name: '', email: '', company_role: 'member', firm_id: '' });
  // System admins have no firm of their own, so they must pick which
  // organization an invited user joins — otherwise the user is created
  // unassigned and shows up in no company group.
  const [firms, setFirms] = useState([]);
  const [inviting, setInviting] = useState(false);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.company_role === 'admin' || currentUser.company_role === 'company_admin' || currentUser.user_type === 'admin';
  const isSystemAdmin = currentUser.user_type === 'admin';

  const isClientUser = currentUser.user_type === 'client';
  const PageHeader = isClientUser ? ClientHeader : Header;

  useEffect(() => { loadTeam(); }, []);

  const loadTeam = async () => {
    try {
      const res = await axios.get(`${API}/user-management/team`, { headers });
      setTeam(res.data);
    } catch (err) { console.error('Error loading team:', err); }
    finally { setLoading(false); }
  };

  const updatePermission = async (userId, permKey, value) => {
    const user = team.find(u => u.id === userId);
    if (!user) return;
    const perms = { ...user.permissions, [permKey]: value };
    setSaving(userId);
    try {
      await axios.put(`${API}/user-management/permissions/${userId}`, perms, { headers });
      setTeam(prev => prev.map(u => u.id === userId ? { ...u, permissions: perms } : u));
    } catch (err) { console.error('Error updating permission:', err); alert('Failed to update'); }
    finally { setSaving(null); }
  };

  const toggleAllPermissions = async (userId, value) => {
    const perms = {};
    PERMISSION_DEFS.forEach(p => { perms[p.key] = value; });
    setSaving(userId);
    try {
      await axios.put(`${API}/user-management/permissions/${userId}`, perms, { headers });
      setTeam(prev => prev.map(u => u.id === userId ? { ...u, permissions: perms } : u));
    } catch (err) { console.error('Error updating permissions:', err); }
    finally { setSaving(null); }
  };

  const updateRole = async (userId, role) => {
    setSaving(userId);
    try {
      await axios.put(`${API}/user-management/role/${userId}`, { company_role: role }, { headers });
      setTeam(prev => prev.map(u => u.id === userId ? { ...u, company_role: role } : u));
    } catch (err) { console.error('Error updating role:', err); alert('Failed to update role'); }
    finally { setSaving(null); }
  };

  const deactivateUser = async (userId, name) => {
    if (!window.confirm(`Deactivate ${name}? They will lose access to the platform.`)) return;
    try {
      await axios.put(`${API}/user-management/deactivate/${userId}`, {}, { headers });
      setTeam(prev => prev.map(u => u.id === userId ? { ...u, is_active: false } : u));
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const reactivateUser = async (userId) => {
    try {
      await axios.put(`${API}/user-management/reactivate/${userId}`, {}, { headers });
      setTeam(prev => prev.map(u => u.id === userId ? { ...u, is_active: true } : u));
    } catch (err) { alert('Failed to reactivate'); }
  };

  useEffect(() => {
    if (!isSystemAdmin || !showInvite || firms.length) return;
    (async () => {
      try {
        // admin-list returns exactly id/name/firm_type, auth-gated.
        const res = await axios.get(`${API}/firms/admin-list`, { headers });
        setFirms(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Could not load organizations:', e);
      }
    })();
    // eslint-disable-next-line
  }, [isSystemAdmin, showInvite]);

  const handleInvite = async () => {
    if (!inviteData.name.trim() || !inviteData.email.trim()) { alert('Name and email are required'); return; }
    if (isSystemAdmin && !inviteData.firm_id) { alert('Choose which organization this user belongs to.'); return; }
    setInviting(true);
    try {
      const res = await axios.post(`${API}/user-management/invite`, inviteData, { headers });
      if (res.data.user) {
        setTeam(prev => [...prev, { ...res.data.user, is_active: true, permissions: {} }]);
      }
      setShowInvite(false);
      setInviteData({ name: '', email: '', company_role: 'member', firm_id: '' });
      alert(res.data.message || 'Invitation sent!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to invite user');
    } finally { setInviting(false); }
  };

  const filtered = useMemo(() => {
    if (filter === 'active') return team.filter(u => u.is_active !== false);
    if (filter === 'deactivated') return team.filter(u => u.is_active === false);
    return team;
  }, [team, filter]);

  const activeCount = team.filter(u => u.is_active !== false).length;
  const deactivatedCount = team.filter(u => u.is_active === false).length;

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  if (loading) return (
    <AppShell variant={isClientUser ? 'client' : 'recruiter'}>
      <div className="h-full overflow-y-auto bg-gray-50">
        <div className="flex items-center justify-center py-24"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
      </div>
    </AppShell>
  );

  return (
    <AppShell variant={isClientUser ? 'client' : 'recruiter'}>
      <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-lg font-bold text-gray-900">User Management</h1>
            <p className="text-xs text-gray-500 mt-0.5">Manage team members and their permissions</p>
          </div>
          {isAdmin && (
            <button onClick={() => setShowInvite(true)}
              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1.5 font-medium">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Invite User
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 mb-4 border-b">
          {[
            { key: 'active', label: 'Active', count: activeCount },
            { key: 'deactivated', label: 'Deactivated', count: deactivatedCount },
            ...(isAdmin ? [
              { key: 'phones', label: 'Phone Numbers', count: null },
              { key: 'ai_usage', label: 'AI Usage', count: null },
            ] : []),
            { key: 'ai_tagging', label: 'AI Tagging', count: null },
            // Settings shows for everyone — non-admins get the Personal tier
            // only; admin tiers are gated inside SettingsHub.
            { key: 'settings', label: 'Settings', count: null },
          ].map(tab => (
            <button key={tab.key} onClick={() => setFilter(tab.key)}
              className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${filter === tab.key ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {tab.label} {tab.count !== null && <span className="text-[10px] ml-1 text-gray-400">({tab.count})</span>}
            </button>
          ))}
        </div>

        {/* Settings tab */}
        {filter === 'settings' && (
          <SettingsHub
            currentUser={currentUser}
            orgPanel={isAdmin ? <OrgSettingsPanel headers={headers} /> : null}
          />
        )}

        {/* AI Usage tab — Suggestions spend for this organization. */}
        {filter === 'ai_usage' && isAdmin && (
          <AIUsagePanel />
        )}

        {/* AI Tagging tab — free-text guidance describing the firm's tagging
            taxonomy, injected into the assistant so it tags consistently. */}
        {filter === 'ai_tagging' && (
          <AITaggingPanel headers={headers} isAdmin={isAdmin} />
        )}

        {/* Phone Numbers tab — admin view of all firm Twilio numbers.
            Sits alongside team management because it's a team-level
            concern (who owns which number). Below the admin table,
            every user sees their own self-service PhoneNumberManager. */}
        {filter === 'phones' && isAdmin && (
          <PhoneNumbersPanel currentUser={currentUser} allUsers={team} headers={headers} />
        )}

        {/* Users table */}
        {filter !== 'settings' && filter !== 'phones' && filter !== 'ai_tagging' && filter !== 'ai_usage' && (filtered.length === 0 ? (
          <div className="text-center py-16">
            <svg className="w-10 h-10 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-sm text-gray-400">No {filter} users</p>
          </div>
        ) : (
          <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-2.5 text-center text-[10px] font-medium text-gray-500 uppercase tracking-wider">Permissions</th>
                  <th className="px-4 py-2.5 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                  <th className="px-4 py-2.5 text-right text-[10px] font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(user => {
                  const isExpanded = expandedUser === user.id;
                  const isSelf = user.id === currentUser.id;
                  const isUserAdmin = user.company_role === 'admin' || user.company_role === 'company_admin' || user.user_type === 'admin';
                  const enabledCount = PERMISSION_DEFS.filter(p => user.permissions?.[p.key] !== false).length;

                  return (
                    <React.Fragment key={user.id}>
                      <tr className={`hover:bg-gray-50 transition-colors ${isExpanded ? 'bg-blue-50/50' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            {user.avatar_url ? (
                              <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold">
                                {user.name?.charAt(0) || '?'}
                              </div>
                            )}
                            <div>
                              <p className="text-xs font-semibold text-gray-900 flex items-center gap-1.5">
                                {user.name}
                                {isSelf && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 font-medium">You</span>}
                              </p>
                              <p className="text-[10px] text-gray-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {isAdmin && !isSelf ? (
                            <select value={user.company_role || 'member'} onChange={(e) => updateRole(user.id, e.target.value)}
                              disabled={saving === user.id}
                              className="text-xs px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-blue-500 bg-white">
                              <option value="company_admin">Admin</option>
                              <option value="member">Member</option>
                            </select>
                          ) : (
                            <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${isUserAdmin ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                              {isUserAdmin ? 'Admin' : 'Member'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isUserAdmin ? (
                            <span className="text-[10px] text-gray-400">Full access</span>
                          ) : (
                            <button onClick={() => setExpandedUser(isExpanded ? null : user.id)}
                              className={`text-[10px] px-2.5 py-1 rounded border transition-colors ${isExpanded ? 'bg-blue-50 border-blue-300 text-blue-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                              {enabledCount}/{PERMISSION_DEFS.length} enabled
                              <svg className={`w-3 h-3 inline-block ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{formatDate(user.created_at)}</td>
                        <td className="px-4 py-3 text-right">
                          {isAdmin && !isSelf && (
                            user.is_active !== false ? (
                              <button onClick={() => deactivateUser(user.id, user.name)}
                                className="text-[10px] px-2 py-1 text-red-600 border border-red-200 rounded hover:bg-red-50">
                                Deactivate
                              </button>
                            ) : (
                              <button onClick={() => reactivateUser(user.id)}
                                className="text-[10px] px-2 py-1 text-green-600 border border-green-200 rounded hover:bg-green-50">
                                Reactivate
                              </button>
                            )
                          )}
                        </td>
                      </tr>

                      {/* Expanded permissions row */}
                      {isExpanded && !isUserAdmin && (
                        <tr>
                          <td colSpan={5} className="px-4 py-3 bg-gray-50/80">
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Permissions for {user.name?.split(' ')[0]}</p>
                              {isAdmin && (
                                <div className="flex gap-1.5">
                                  <button onClick={() => toggleAllPermissions(user.id, true)}
                                    className="text-[10px] px-2 py-0.5 text-green-600 border border-green-200 rounded hover:bg-green-50">Enable All</button>
                                  <button onClick={() => toggleAllPermissions(user.id, false)}
                                    className="text-[10px] px-2 py-0.5 text-red-600 border border-red-200 rounded hover:bg-red-50">Disable All</button>
                                </div>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                              {PERMISSION_DEFS.map(perm => {
                                const enabled = user.permissions?.[perm.key] !== false;
                                return (
                                  <div key={perm.key} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-white transition-colors">
                                    <div>
                                      <p className="text-xs font-medium text-gray-800">{perm.label}</p>
                                      <p className="text-[10px] text-gray-400">{perm.desc}</p>
                                    </div>
                                    {isAdmin ? (
                                      <button onClick={() => updatePermission(user.id, perm.key, !enabled)}
                                        disabled={saving === user.id}
                                        className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${enabled ? 'bg-blue-600' : 'bg-gray-300'} ${saving === user.id ? 'opacity-50' : ''}`}>
                                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'left-[18px]' : 'left-0.5'}`} />
                                      </button>
                                    ) : (
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                        {enabled ? 'On' : 'Off'}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Invite User Modal */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="font-semibold text-gray-900">Invite Team Member</h3>
              <button onClick={() => { setShowInvite(false); setInviteData({ name: '', email: '', company_role: 'member' }); }} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Full Name</label>
                <input type="text" value={inviteData.name} onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                  placeholder="John Smith" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" autoFocus />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email Address</label>
                <input type="email" value={inviteData.email} onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  placeholder="john@company.com" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Role</label>
                {isSystemAdmin && (
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Organization <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={inviteData.firm_id}
                      onChange={(e) => setInviteData({ ...inviteData, firm_id: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    >
                      <option value="">Select an organization…</option>
                      {firms.map(f => (
                        <option key={f.id} value={f.id}>
                          {f.name}{f.firm_type ? ` (${f.firm_type})` : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Required — you aren't tied to one organization, so the user would otherwise be created unassigned.
                    </p>
                  </div>
                )}

                <select value={inviteData.company_role} onChange={(e) => setInviteData({ ...inviteData, company_role: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="member">Member</option>
                  <option value="company_admin">Admin</option>
                </select>
              </div>
              <p className="text-[10px] text-gray-400">They'll receive an email with a temporary password to sign in.</p>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => { setShowInvite(false); setInviteData({ name: '', email: '', company_role: 'member' }); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={handleInvite} disabled={inviting || !inviteData.name.trim() || !inviteData.email.trim() || (isSystemAdmin && !inviteData.firm_id)}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                {inviting ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </AppShell>
  );
};

// ── Phone Numbers admin panel ────────────────────────────────────────
// Two-part view:
//   1. Admin table: every user in the firm with their assigned Twilio
//      number (if any) + release / reassign actions.
//   2. Per-user self-service: below the admin table, the existing
//      PhoneNumberManager renders so the current user can get/manage
//      their own number without leaving the page.
//
// Reads GET /api/outreach/phone-numbers (firm-scoped, joined with
// user_name + user_email). Reassigns via PUT /:id/assign, releases
// via DELETE /:id.
const PhoneNumbersPanel = ({ currentUser, allUsers, headers }) => {
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [releasing, setReleasing] = useState(null);
  const [reassignTarget, setReassignTarget] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/outreach/phone-numbers`, { headers });
      setNumbers(res.data || []);
    } catch (e) {
      console.error('[banyak-phone-admin] load failed', e);
      setError('Failed to load phone numbers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startReassign = (numId) => {
    setAssigning(numId);
    setReassignTarget('');
  };

  const confirmReassign = async (numId) => {
    if (!reassignTarget) return;
    try {
      await axios.put(`${API}/outreach/phone-numbers/${numId}/assign`,
        { user_id: parseInt(reassignTarget, 10) },
        { headers }
      );
      setAssigning(null);
      setReassignTarget('');
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Reassignment failed');
    }
  };

  const releaseNumber = async (numId, phoneNumber) => {
    if (!window.confirm(`Release ${phoneNumber}? It will be removed from your account and returned to Twilio.`)) return;
    setReleasing(numId);
    try {
      await axios.delete(`${API}/outreach/phone-numbers/${numId}`, { headers });
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Release failed');
    } finally {
      setReleasing(null);
    }
  };

  const formatPhone = (p) => {
    if (!p) return '';
    const cleaned = p.replace(/[^+\d]/g, '');
    if (cleaned.length === 12 && cleaned.startsWith('+1')) {
      return `+1 (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(8)}`;
    }
    return p;
  };

  // Users in the current firm without an active number — these are the
  // candidates for reassignment. Filter defensively in case allUsers
  // spans multiple firms (shouldn't at this scope but doesn't hurt).
  const usersWithoutNumber = (allUsers || []).filter(u => {
    if (u.firm_id !== undefined && currentUser?.firm_id !== undefined && u.firm_id !== currentUser.firm_id) return false;
    return !numbers.some(n => n.user_id === u.id && n.status === 'active');
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900 mb-1">Team Phone Numbers</h2>
        <p className="text-xs text-gray-500">All Twilio numbers owned by your organization. Reassign or release as needed.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-xs text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
        </div>
      ) : numbers.length === 0 ? (
        <div className="bg-white rounded-lg border border-dashed p-10 text-center">
          <svg className="w-10 h-10 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <p className="text-sm text-gray-500 font-medium">No phone numbers in your organization yet</p>
          <p className="text-xs text-gray-400 mt-1">Users can get one below to start sending SMS and making calls.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Phone Number</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Assigned To</th>
                <th className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Added</th>
                <th className="text-right px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {numbers.map(num => {
                const isBeingReassigned = assigning === num.id;
                return (
                  <tr key={num.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5">
                      <span className="font-mono text-sm font-medium text-gray-900">{formatPhone(num.phone_number)}</span>
                      <div className="flex gap-1 mt-0.5">
                        {num.capabilities?.sms && <span className="text-[9px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700">SMS</span>}
                        {num.capabilities?.voice && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Voice</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {isBeingReassigned ? (
                        <div className="flex items-center gap-2">
                          <select
                            value={reassignTarget}
                            onChange={(e) => setReassignTarget(e.target.value)}
                            className="text-xs border rounded px-2 py-1 flex-1"
                          >
                            <option value="">Pick a user…</option>
                            {usersWithoutNumber.map(u => (
                              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                            ))}
                          </select>
                          <button
                            onClick={() => confirmReassign(num.id)}
                            disabled={!reassignTarget}
                            className="px-2 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => { setAssigning(null); setReassignTarget(''); }}
                            className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div>
                          <div className="text-sm text-gray-900">{num.user_name || <span className="text-gray-400 italic">Unassigned</span>}</div>
                          {num.user_email && <div className="text-xs text-gray-500">{num.user_email}</div>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">
                      {num.created_at ? new Date(num.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {!isBeingReassigned && (
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={() => startReassign(num.id)}
                            className="px-2 py-1 text-xs border rounded text-gray-600 hover:bg-gray-50"
                          >
                            Reassign
                          </button>
                          <button
                            onClick={() => releaseNumber(num.id, num.phone_number)}
                            disabled={releasing === num.id}
                            className="px-2 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50 disabled:opacity-50"
                          >
                            {releasing === num.id ? 'Releasing…' : 'Release'}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Users without a number — surfaced so admin can see who still
          needs one. Only visible when there's at least one team member
          without a number, keeping the page quiet when everyone's set. */}
      {!loading && usersWithoutNumber.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-xs text-amber-900">
              <strong>{usersWithoutNumber.length} team member{usersWithoutNumber.length === 1 ? '' : 's'} without a phone number:</strong>{' '}
              {usersWithoutNumber.slice(0, 5).map(u => u.name).join(', ')}
              {usersWithoutNumber.length > 5 && ` and ${usersWithoutNumber.length - 5} more`}
              . They can get one from their own <a href="/profile" className="underline">Profile page</a>, or you can purchase and reassign one from here.
            </div>
          </div>
        </div>
      )}

      {/* Per-user self-service — everyone (including admin) can manage
          their own single number here without navigating away. */}
      <div className="bg-white rounded-lg border p-6">
        <PhoneNumberManager />
      </div>
    </div>
  );
};

// Org-wide settings panel (admin only) — LinkedIn sync cooldown
const OrgSettingsPanel = ({ headers }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cooldownDays, setCooldownDays] = useState(30);
  const [savedMessage, setSavedMessage] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/org-settings`, { headers });
        setCooldownDays(res.data.linkedin_sync_cooldown_days ?? 30);
      } catch (err) {
        console.error('Failed to load org settings:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSavedMessage('');
    try {
      const res = await axios.put(`${API}/org-settings`,
        { linkedin_sync_cooldown_days: parseInt(cooldownDays) || 0 },
        { headers }
      );
      setCooldownDays(res.data.linkedin_sync_cooldown_days ?? 30);
      setSavedMessage('✓ Settings saved');
      setTimeout(() => setSavedMessage(''), 3000);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.detail || err.message;
      setSavedMessage('Error: ' + msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6">
        <div className="animate-pulse text-gray-400 text-sm">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-white border rounded-lg p-6">
        <div className="flex items-start gap-3 mb-5">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-[#0A66C2]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">LinkedIn Sync Settings</h2>
            <p className="text-xs text-gray-500 mt-0.5">Control how often firm data can be re-synced from LinkedIn for your organization.</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              Sync Cooldown (days)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="365"
                value={cooldownDays}
                onChange={(e) => setCooldownDays(e.target.value)}
                className="w-24 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="text-xs text-gray-500">days between syncs per firm</span>
            </div>
            <p className="mt-1.5 text-xs text-gray-500">
              A firm cannot be re-synced from LinkedIn until this many days have passed since the last sync.
              Set to <strong>0</strong> to disable the cooldown. Recommended: 7–30 days to control API costs.
            </p>
          </div>

          <div className="pt-3 border-t border-gray-100 flex items-center gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            {savedMessage && (
              <span className={`text-xs ${savedMessage.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
                {savedMessage}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-xs text-blue-900">
            <strong>How it works:</strong> Each LinkedIn sync uses a Bright Data API credit (~$0.0015 per firm).
            The cooldown prevents accidental re-syncing and keeps costs predictable across your team.
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagementPage;
